import { useEffect } from "react";

export interface SeoOptions {
  title: string;
  description?: string;
  keywords?: string;
  path?: string;
  jsonLd?: object[];
}

const BASE_URL = "https://dyshorts.github.io";
const DEFAULT_DESCRIPTION =
  "抖漫聚合短剧化动态漫画，竖屏全屏观看更沉浸，自带配音与特效，追更不迷路。想看抖漫短剧、抖漫动态漫画，点开即看。";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * 页面级 SEO 注入 hook：更新 title、meta、canonical、OG、Twitter、JSON-LD
 */
export function useSeo({ title, description, keywords, path = "/", jsonLd = [] }: SeoOptions) {
  useEffect(() => {
    const url = `${BASE_URL}${path}`;
    const desc = description || DEFAULT_DESCRIPTION;

    document.title = title;
    upsertMeta("name", "description", desc);
    if (keywords) upsertMeta("name", "keywords", keywords);
    upsertLink("canonical", url);

    // Open Graph
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:site_name", "抖漫");
    upsertMeta("property", "og:locale", "zh_CN");
    upsertMeta("property", "og:type", "website");

    // Twitter
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);

    // JSON-LD
    const scriptId = "page-jsonld";
    document.getElementById(scriptId)?.remove();
    if (jsonLd.length > 0) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // 清理
    return () => {
      document.getElementById(scriptId)?.remove();
    };
  }, [title, description, keywords, path, JSON.stringify(jsonLd)]);
}